import { convertUaeToUtc } from "@utils/date-time";
import type { TFunction } from "i18next";
import { z } from "zod";

/**
 * Dry-bulk nomination form (DBVN-0005). Saving a draft checks formats only (VN-10, D5): every header field may be
 * empty. A cargo line, activity or resource is added only when its side drawer validates (G-2), so the lists hold
 * complete items. Required fields and "no past dates" on the header are submit rules (DBVN-0007).
 */
export const LIMITS = {
  MAX_ARRIVAL_DRAFT_DIGITS: 15,
  REMARKS: 1000,
  VIA_NUMBER: 50,
  BERTH_CODE: 50,
  QUANTITY_DIGITS: 10,
  OTHER_TEXT: 30,
  OTHER_SPECIFY: 50,
  COUNT_DIGITS: 3,
  MEASURE_DIGITS: 6,
} as const;

export const PORT_CODES = ["POF", "POD"] as const;
export const OPERATION_TYPES = ["LOADING", "DISCHARGE"] as const;
export const ACTIVITY_TYPES = [
  "INWARD_CLEARANCE",
  "OUTWARD_CLEARANCE",
  "LAYBY",
  "REPAIR",
  "TOW_BARGE",
  "US_NAVY",
  "VETTING_INSPECTION",
  "CREW_CHANGE",
  "OTHER",
] as const;
export const RESOURCE_OWNERS = ["OPERATIONS_DEPARTMENT", "VESSEL"] as const;
export const RESOURCE_TYPES = ["GEARED", "GEARLESS"] as const;
export const DOCUMENT_TYPES = [
  "CERTIFICATE",
  "PERMIT",
  "DOCUMENT",
  "INSURANCE",
  "VESSEL_MASTER_SOF",
  "OTHERS",
] as const;

const LANG = "vessel-nomination.dry-bulk.add.validation";

const maxDigits = (digits: number) => 10 ** digits - 1;

/** FormItem's number input commits strings (with thousand separators); empty means "not entered". */
export const toNumber = (value: unknown) => {
  if (value === "" || value === null || value === undefined) return undefined;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/,/g, ""));
    return Number.isNaN(parsed) ? value : parsed;
  }
  return value;
};

const optionalWholeNumber = (t: TFunction, digits: number) =>
  z.preprocess(
    toNumber,
    z
      .number({ invalid_type_error: t(`${LANG}.whole-number`) })
      .int(t(`${LANG}.whole-number`))
      .min(0, t(`${LANG}.whole-number`))
      .max(maxDigits(digits), t(`${LANG}.max-digits`, { count: digits }))
      .optional()
      .nullable(),
  );

const requiredText = (t: TFunction) =>
  z
    .string()
    .trim()
    .min(1, t(`${LANG}.required`));

export const getCargoLineSchema = (t: TFunction) =>
  z
    .object({
      operationType: z.enum(OPERATION_TYPES, {
        errorMap: () => ({ message: t(`${LANG}.required`) }),
      }),
      cargoCode: requiredText(t),
      quantityMt: z.preprocess(
        toNumber,
        z
          .number({
            invalid_type_error: t(`${LANG}.whole-number`),
            required_error: t(`${LANG}.required`),
          })
          .int(t(`${LANG}.whole-number`))
          .min(0, t(`${LANG}.whole-number`))
          .max(
            maxDigits(LIMITS.QUANTITY_DIGITS),
            t(`${LANG}.max-digits`, { count: LIMITS.QUANTITY_DIGITS }),
          ),
      ),
      shipperCompanyCode: z.string().optional(),
      stockPileCode: z.string().optional(),
      receiverCompanyCode: z.string().optional(),
    })
    .superRefine((line, ctx) => {
      const loading = line.operationType === "LOADING";
      const required = loading
        ? (["shipperCompanyCode", "stockPileCode"] as const)
        : (["receiverCompanyCode"] as const);
      required.forEach((field) => {
        if (!line[field]?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: t(`${LANG}.required`),
          });
        }
      });
    })
    .transform((line) =>
      line.operationType === "LOADING"
        ? { ...line, receiverCompanyCode: undefined }
        : { ...line, shipperCompanyCode: undefined, stockPileCode: undefined },
    );

export const getActivitySchema = (t: TFunction) =>
  z
    .object({
      activityType: z.enum(ACTIVITY_TYPES, {
        errorMap: () => ({ message: t(`${LANG}.required`) }),
      }),
      otherText: z
        .string()
        .max(LIMITS.OTHER_TEXT, t(`${LANG}.max-chars`, { count: LIMITS.OTHER_TEXT }))
        .optional(),
      dateFrom: z.date({
        invalid_type_error: t(`${LANG}.required`),
        required_error: t(`${LANG}.required`),
      }),
      dateTo: z.date({
        invalid_type_error: t(`${LANG}.required`),
        required_error: t(`${LANG}.required`),
      }),
    })
    .superRefine((activity, ctx) => {
      if (activity.activityType === "OTHER" && !activity.otherText?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["otherText"],
          message: t(`${LANG}.required`),
        });
      }
      if (activity.dateTo < activity.dateFrom) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dateTo"],
          message: t(`${LANG}.date-to-before-from`),
        });
      }
    });

export const getResourceSchema = (t: TFunction) =>
  z
    .object({
      owner: z.enum(RESOURCE_OWNERS),
      type: z.enum(RESOURCE_TYPES, { errorMap: () => ({ message: t(`${LANG}.required`) }) }),
      craneCount: optionalWholeNumber(t, LIMITS.COUNT_DIGITS),
      craneCapacityMt: optionalWholeNumber(t, LIMITS.MEASURE_DIGITS),
      craneSwl: optionalWholeNumber(t, LIMITS.MEASURE_DIGITS),
      cranesGoodCondition: z.boolean().optional().nullable(),
      grabCount: optionalWholeNumber(t, LIMITS.COUNT_DIGITS),
      grabSizeCbm: optionalWholeNumber(t, LIMITS.MEASURE_DIGITS),
      grabSwl: optionalWholeNumber(t, LIMITS.MEASURE_DIGITS),
      grabsGoodCondition: z.boolean().optional().nullable(),
      resourceCount: optionalWholeNumber(t, LIMITS.COUNT_DIGITS),
    })
    .superRefine((resource, ctx) => {
      if (
        resource.type === "GEARLESS" &&
        (resource.resourceCount === undefined || resource.resourceCount === null)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["resourceCount"],
          message: t(`${LANG}.required`),
        });
      }
    });

export const getAttachmentSchema = (t: TFunction) =>
  z
    .object({
      documentType: z.enum(DOCUMENT_TYPES, {
        errorMap: () => ({ message: t(`${LANG}.required`) }),
      }),
      otherSpecify: z
        .string()
        .max(LIMITS.OTHER_SPECIFY, t(`${LANG}.max-chars`, { count: LIMITS.OTHER_SPECIFY }))
        .optional(),
      issueDate: z.date().optional().nullable(),
      expiryDate: z.date({
        invalid_type_error: t(`${LANG}.required`),
        required_error: t(`${LANG}.required`),
      }),
      fileUrl: requiredText(t),
      documentName: z.string().optional(),
    })
    .superRefine((attachment, ctx) => {
      if (attachment.documentType === "OTHERS" && !attachment.otherSpecify?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["otherSpecify"],
          message: t(`${LANG}.required`),
        });
      }
    });

export type CargoLineFormData = z.input<ReturnType<typeof getCargoLineSchema>>;
/** A complete line as stored in the form (the drawer schema already dropped the fields of the other type). */
export type CargoLine = CargoLineFormData;
export type ActivityFormData = z.input<ReturnType<typeof getActivitySchema>>;
export type ResourceFormData = z.input<ReturnType<typeof getResourceSchema>>;
/** A stored attachment row is edited in place on the form, so each row carries the drawer's rules (VN-09). */
const getStoredAttachmentSchema = (t: TFunction) =>
  z
    .object({
      documentType: z.enum(DOCUMENT_TYPES, {
        errorMap: () => ({ message: t(`${LANG}.required`) }),
      }),
      otherSpecify: z
        .string()
        .max(LIMITS.OTHER_SPECIFY, t(`${LANG}.max-chars`, { count: LIMITS.OTHER_SPECIFY }))
        .optional(),
      issueDate: z.string().optional().nullable(),
      expiryDate: z.string().min(1, t(`${LANG}.required`)),
      fileUrl: requiredText(t),
      documentName: z.string().optional(),
    })
    .superRefine((attachment, ctx) => {
      if (attachment.documentType === "OTHERS" && !attachment.otherSpecify?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["otherSpecify"],
          message: t(`${LANG}.required`),
        });
      }
    });

export type AttachmentFormData = z.input<ReturnType<typeof getAttachmentSchema>>;

/** List items keep dates as ISO strings so the persisted form survives a reload unchanged. */
export type StoredActivity = Omit<ActivityFormData, "dateFrom" | "dateTo"> & {
  dateFrom: string;
  dateTo: string;
};
export type StoredAttachment = Omit<AttachmentFormData, "issueDate" | "expiryDate"> & {
  issueDate?: string | null;
  expiryDate: string;
};

export const getDryBulkNominationSchema = (t: TFunction) =>
  z.object({
    portCode: z.union([z.enum(PORT_CODES), z.literal("")]).optional(),
    lastPortCode: z.string().optional(),
    nextPortCode: z.string().optional(),
    initialEta: z.date().optional().nullable(),
    maxArrivalDraft: optionalWholeNumber(t, LIMITS.MAX_ARRIVAL_DRAFT_DIGITS),
    expectedBerthCode: z.string().max(LIMITS.BERTH_CODE).optional(),
    expectedBerthName: z.string().optional(),
    purposeOfVisitCode: z.string().optional(),
    viaNumber: z
      .string()
      .max(LIMITS.VIA_NUMBER, t(`${LANG}.max-chars`, { count: LIMITS.VIA_NUMBER }))
      .regex(/^[0-9]*$/, t(`${LANG}.digits-only`))
      .optional(),
    remarks: z
      .string()
      .max(LIMITS.REMARKS, t(`${LANG}.max-chars`, { count: LIMITS.REMARKS }))
      .optional(),
    cargoLines: z.array(z.custom<CargoLine>()),
    activities: z.array(z.custom<StoredActivity>()),
    resources: z.array(z.custom<ResourceFormData>()),
    attachments: z.array(getStoredAttachmentSchema(t)),
  });

export type DryBulkNominationFormData = z.input<ReturnType<typeof getDryBulkNominationSchema>>;

const isBeforeNow = (value: Date | string | null | undefined, now: Date) =>
  !!value && new Date(value).getTime() < now.getTime();

/**
 * Request Nomination rules (DBVN-0007, VD-46 / VN-13): the draft rules plus every field not marked optional,
 * at least one cargo line or non-cargo operation (VD-65), the Expected Berth (VD-71), and no Initial ETA or activity date in the past. The server checks the
 * same rules (and no vessel-call rule since VD-68); this mirror marks the fields before the request is sent.
 */
export const getDryBulkSubmissionSchema = (t: TFunction, now: () => Date = () => new Date()) =>
  getDryBulkNominationSchema(t).superRefine((values, ctx) => {
    const required = t(`${LANG}.required`);
    const at = now();
    (["portCode", "lastPortCode", "nextPortCode"] as const).forEach((field) => {
      if (!values[field]) ctx.addIssue({ code: "custom", path: [field], message: required });
    });
    if (values.maxArrivalDraft === undefined || values.maxArrivalDraft === null) {
      ctx.addIssue({ code: "custom", path: ["maxArrivalDraft"], message: required });
    }
    if (!values.initialEta) {
      ctx.addIssue({ code: "custom", path: ["initialEta"], message: required });
    } else if (isBeforeNow(convertUaeToUtc(values.initialEta), at)) {
      ctx.addIssue({ code: "custom", path: ["initialEta"], message: t(`${LANG}.not-in-past`) });
    }
    if (!values.expectedBerthCode) {
      ctx.addIssue({ code: "custom", path: ["expectedBerthCode"], message: required });
    }
    if (values.cargoLines.length === 0 && values.activities.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["cargoLines"],
        message: t(`${LANG}.cargo-line-required`),
      });
    }
    values.activities.forEach((activity, index) => {
      if (isBeforeNow(activity.dateFrom, at) || isBeforeNow(activity.dateTo, at)) {
        ctx.addIssue({
          code: "custom",
          path: ["activities", index],
          message: t(`${LANG}.not-in-past`),
        });
      }
    });
  });

export const emptyDryBulkNominationForm = (): DryBulkNominationFormData => ({
  portCode: "",
  lastPortCode: "",
  nextPortCode: "",
  initialEta: null,
  maxArrivalDraft: undefined,
  remarks: "",
  expectedBerthCode: "",
  expectedBerthName: "",
  purposeOfVisitCode: "",
  viaNumber: "",
  cargoLines: [],
  activities: [],
  resources: [],
  attachments: [],
});
